/**
 * Was von einer Quelle in den Stream geht -- und was am Ausgang davon
 * ankommt.
 *
 * Zwei Ebenen, weil die Regie zwei verschiedene Fragen hat:
 *
 * - **Pro Quelle** (`SourceFeed`): Mikrofon, Ton des geteilten Bildschirms,
 *   Kamerabild. Das schaltet das *sendende Geraet* an seinen eigenen Spuren
 *   ab (`track.enabled = false`), also fliesst schon nichts mehr durch die
 *   Leitung. Wer stumm ist, ist damit bei Zuschauern, im RTMP-Ausgang und im
 *   Regie-Monitor gleichzeitig stumm -- es gibt keinen Weg daran vorbei.
 * - **Vom Sender selbst** (`SourceFeed.own`): dieselben Spuren, abgeschaltet
 *   auf der Senden-Seite -- fuer den Moment, in dem die Regie nicht schnell
 *   genug hinterherkommt. Bewusst ein eigener Schalter und nicht derselbe:
 *   was der Sender ausschaltet, kann nur er wieder einschalten, und die Regie
 *   holt keine Kamera gegen seinen Willen zurueck. Umgekehrt hebt der Sender
 *   ein „aus" der Regie nicht auf. Raus geht nur, was beide zulassen.
 * - **Am Ausgang** (`OutputState`): der Griff, der gilt, egal welche Kamera
 *   gerade im Hauptbild liegt. Ein fremdes Handy on air laesst sich nicht
 *   schnell genug ueber drei Ecken stummschalten; dieser Schalter wirkt
 *   sofort, weil er am Programm haengt und nicht an der Quelle.
 *
 * Warum der Zustand auf dem Server liegt und nicht im Regie-Tab: nur so
 * wissen Zuschauersicht und Renderer, dass das Bild *absichtlich* weg ist --
 * dann zeigen sie das Standardbild statt Schwarz. Und nur so steht nach
 * einem F5 in der Regie noch dasselbe da wie vorher, genau wie beim
 * Hauptbild und beim Look.
 */

/* ---------------------------------------------------------- pro Quelle */

export interface SourceFeed {
  /** Mikrofon dieses Geraets. Aus heisst stumm -- fuer alle. */
  mic: boolean;
  /**
   * Ton des geteilten Bildschirms. Bei einer Kamera ohne Bedeutung, dort
   * gibt es nur das Mikrofon.
   */
  system: boolean;
  /** Kamerabild. Aus heisst: Standardbild statt Bild dieser Quelle. */
  cam: boolean;
  /** Was der Sender selbst abgeschaltet hat -- nur er schaltet es wieder ein. */
  own: OwnFeed;
}

/** Die Schalter auf der Senden-Seite. Bildschirmton gibt es dort nicht. */
export interface OwnFeed {
  mic: boolean;
  cam: boolean;
}

export const defaultOwnFeed = (): OwnFeed => ({ mic: true, cam: true });

export const defaultFeed = (): SourceFeed => ({ mic: true, system: true, cam: true, own: defaultOwnFeed() });

/** Die Schalter der Regie -- `own` gehoert dem Sender und ist hier nicht dabei. */
export type FeedSwitch = 'mic' | 'system' | 'cam';

export type FeedPatch = Partial<Pick<SourceFeed, FeedSwitch>>;

export type OwnFeedPatch = Partial<OwnFeed>;

/** Die drei Schalter der Regie mit Beschriftung. */
export const FEED_SWITCHES: { id: FeedSwitch; label: string; icon: string; hint: string }[] = [
  { id: 'mic', label: 'Mikro', icon: '🎤', hint: 'Mikrofon dieser Quelle — aus heißt stumm im Stream.' },
  { id: 'cam', label: 'Kamera', icon: '🎥', hint: 'Bild abblenden — im Stream läuft dann das Standardbild.' },
  {
    id: 'system',
    label: 'Bildschirmton',
    icon: '🔊',
    hint: 'Ton des geteilten Bildschirms — nur bei Bildschirmübertragung.',
  },
];

/**
 * Ein Patch fuer genau einen Schalter. Ausgeschrieben statt `{ [id]: on }`,
 * weil ein berechneter Schluessel den Typ auf eine Index-Signatur aufweicht --
 * und dann faengt hier nichts mehr einen Tippfehler im Feldnamen.
 */
export const feedPatch = (id: FeedSwitch, on: boolean): FeedPatch =>
  id === 'mic' ? { mic: on } : id === 'cam' ? { cam: on } : { system: on };

/** Geht das Mikrofon raus? Nur, wenn Regie *und* Sender es zulassen. */
export const micOn = (feed: SourceFeed | null | undefined): boolean => Boolean(feed?.mic && feed.own.mic);

/** Geht das Kamerabild raus? Nur, wenn Regie *und* Sender es zulassen. */
export const camOn = (feed: SourceFeed | null | undefined): boolean => Boolean(feed?.cam && feed.own.cam);

/* ------------------------------------------------------------- Ausgang */

export interface OutputState {
  /** Master-Stumm: aus dem Stream kommt kein Ton, egal welche Quelle laeuft. */
  muted: boolean;
  /** Master-Abblende: Standardbild statt Hauptbild. */
  blanked: boolean;
}

export const defaultOutput = (): OutputState => ({ muted: false, blanked: false });

export type OutputPatch = Partial<OutputState>;

/* ------------------------------------------------- was daraus folgt */

/**
 * Hoert man diese Quelle im Stream? Die Spuren sind beim Sender schon
 * abgeschaltet -- das hier ist die *zweite* Sicherung an der Empfangsseite,
 * damit ein Mischer nicht ins Leere mischt und die Oberflaeche sagen kann,
 * warum es still ist.
 */
export const feedAudible = (
  feed: SourceFeed | null | undefined,
  output: OutputState,
  /**
   * Ob der Bildschirmton ueberhaupt mitzaehlt. Bei einer Kamera nicht -- dort
   * ist „Mikro aus" gleichbedeutend mit stumm, und genau das soll die
   * Oberflaeche dann auch sagen koennen.
   */
  kind: 'camera' | 'screen' = 'camera',
): boolean => !output.muted && Boolean(feed && (micOn(feed) || (kind === 'screen' && feed.system)));

/**
 * Hoert man das kleine Fenster? Was im Bild ist, ist auch zu hoeren -- also
 * dieselbe Regel wie beim Hauptbild, mit einer Ausnahme: kommt das kleine
 * Fenster vom selben Geraet wie das Hauptbild (Regie: Bildschirm gross,
 * Kamera klein), zaehlt nur der Ton des Hauptbilds. Beide Spuren tragen dann
 * dasselbe Mikrofon, und zweimal gemischt klingt es hallig.
 */
export const pipAudible = (
  pip: { feed: SourceFeed; kind: 'camera' | 'screen'; connectionId: string } | null | undefined,
  main: { connectionId: string } | null | undefined,
  output: OutputState,
): boolean => Boolean(pip && pip.connectionId !== main?.connectionId && feedAudible(pip.feed, output, pip.kind));

/** Sieht man diese Quelle im Stream, oder laeuft das Standardbild? */
export const feedVisible = (feed: SourceFeed | null | undefined, output: OutputState): boolean =>
  !output.blanked && camOn(feed);

/**
 * Warum das Standardbild laeuft. `null` heisst: es laeuft nicht.
 *
 * Die Unterscheidung ist keine Kosmetik -- „die Regie hat abgeblendet" und
 * „diese Kamera ist abgeblendet" sind fuer den Zuschauer dasselbe Bild, fuer
 * die Regie aber zwei verschiedene Knoepfe, die sie zuruecknehmen muss.
 * `own` ist der dritte Fall: der Sender hat selbst abgeblendet -- da hilft
 * kein Knopf der Regie, nur Warten oder Nachfragen.
 */
export type StandbyReason = 'output' | 'cam' | 'own' | null;

export const standbyReason = (feed: SourceFeed | null | undefined, output: OutputState): StandbyReason =>
  output.blanked ? 'output' : feed && !feed.cam ? 'cam' : feed && !feed.own.cam ? 'own' : null;
